import { useEffect, useState } from "react";
import { ETypeOfNotification, InputColumnTableProps, InputSwitch, InputTable, InputTextCustom, Modal, setNotification } from "medere-ui-framework";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import { defaultEstablishment, Establishment } from "../../../../../interface/interface";
import { activateEstablishment, inactivateEstablishment, loadAllEstablishments, saveEstablishment } from "../../../../../servicesHooks/Location/useLocationConfigService";
import { useLocationConfigContext } from "../../../context/LocationConfigContext";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import LocationInlineEditActions from "../../../components/LocationInlineEditActions";

interface EstablishmentTableProps {
    establishments: Establishment[];
    onChange: (updatedEstablishments: Establishment[]) => void;
    createSector?: (establishment: Establishment) => void;
    onDelete: (_idx: number) => void;
}

export function EstablishmentTable({ establishments, onChange, createSector, onDelete }: EstablishmentTableProps) {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);
    const { setCallersToEstablishments } = useLocationConfigContext();
    const [establishmentList, setEstablishmentList] = useState<Establishment[]>([])

    const [establishmentItem, setEstablishmentItem] = useState<Establishment>(defaultEstablishment);
    const [establishmentErrors, setEstablishmentErrors] = useState<{ name?: string; address?: string }>({});
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);
    const isEditing = editIndex !== null;

    const resetEditor = () => {
        setEstablishmentItem(defaultEstablishment);
        setEstablishmentErrors({});
        setEditIndex(null);
    };

    useEffect(() => {
        if (establishments)
            setEstablishmentList(establishments) //Uso lista auxiliar dentro de la tabla para todo menos para agregar y borrar, para evitar el caso en que decida editar establecimiento y que luego, sin volver a agregar el establecimiento se pase a otra pestaña (Borra todo lo asociado a ese establecimiento). Con una auxiliar ya no pasa y actua todo lo demás con normalidad
    }, [establishments])

    const handleAdd = async (): Promise<boolean> => {
        const errors = validateEstablishment(establishmentItem);
        setEstablishmentErrors(errors);
        if (Object.keys(errors).length > 0) return false;

        try {
            const created = await saveEstablishment(establishmentItem);
            if (!isEditing){
                const completedEstablishment = {
                    ...created,
                    boxes: [],
                    sectors: [],
                    offices: [],
                    callers: []
                };

                const updatedEstablishments = [...establishmentList, completedEstablishment];
                setEstablishmentList(updatedEstablishments) //Actualizo la lista que muestra la tabla
                onChange(updatedEstablishments); //Actualizo la lista real que se utiliza en las demás pestañas
            } else {
                loadAllEstablishments().then((res) => {
                    setEstablishmentList(res)
                    setCallersToEstablishments(res)
                    onChange(res);
                }).catch((err) => {
                    console.error(err)
                    dispatch(setNotification({ title: "Ocurrió un error", desc: 'No se pudieron cargar los establecimientos', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                });
            }
            
            dispatch(setNotification({
                title: !isEditing ? "Creación exitosa" : "Edición exitosa",
                desc: !isEditing ? "Se ha creado el establecimiento" : "Se ha editado el establecimiento",
                icon: "pi pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO
            }));
            resetEditor();
            return true;
        } catch (e) {
            dispatch(setNotification({
                title: "Error",
                desc: "No se pudo crear el establecimiento",
                icon: "pi pi-times",
                typeOfNotification: ETypeOfNotification.ERROR
            }));
            return false;
        }
    };

    const handleEdit = (item: Establishment, index: number) => {
        setEstablishmentItem(item);
        setEstablishmentErrors({});
        setEditIndex(index);
    };

    const cancelEdit = () => {
        resetEditor();
    };

    function closeMobileModal() {
        setMobileModalVisible(false);
        resetEditor();
    }

    function openAddModal() {
        resetEditor();
        setMobileModalVisible(true);
    }

    function openEditModal(item: Establishment, index: number) {
        handleEdit(item, index);
        setEstablishmentErrors({});
        setMobileModalVisible(true);
    }

    const handleToggleActive = async (item: Establishment, index: number) => {
        try {
            if (item.active) {
                await inactivateEstablishment(item.buildingid);
            } else {
                await activateEstablishment(item.buildingid);
            }

            const updated = [...establishmentList];
            updated[index] = { ...item, active: !item.active };
            setEstablishmentList(updated) //Actualizo la lista que muestra la tabla
            onChange(updated); //Actualizo la lista real que se utiliza en las demás pestañas
        } catch (e) {
            dispatch(setNotification({
                title: "Error",
                desc: "No se pudo cambiar el estado del establecimiento",
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR
            }));
        }
    };


    const handleAddSector = (item: Establishment) => {
        if (createSector) createSector(item);
    };

    const validateEstablishment = (est: Establishment): { name?: string; address?: string } => {
        const errors: { name?: string; address?: string } = {};
        if (!est.buildingname.trim()) errors.name = "El nombre es requerido";
        if (!est.buildingaddress.trim()) errors.address = "La dirección es requerida";
        return errors;
    };

    const columns: InputColumnTableProps<Establishment>[] = [
        {
            header: "Nombre",
            className: "location-inputtable-fluid-col location-col-est-name",
            body: (item) => item.buildingname,
            input: (
                <InputTextCustom
                    key={"inputEstNameId"}
                    className={(establishmentErrors.name ? "":"border-grey")+" w-full"}
                    value={establishmentItem.buildingname}
                    onChange={(e) => {
                        setEstablishmentItem({ ...establishmentItem, buildingname: e.target.value })
                        setEstablishmentErrors({ ...establishmentErrors, name: "" })
                    }}
                    error={!!establishmentErrors.name}
                    caption={establishmentErrors.name}
                />
            ),
        },
        {
            header: "Dirección",
            className: "location-inputtable-fluid-col location-col-est-address",
            body: (item) => item.buildingaddress,
            input: (
                <InputTextCustom
                    key={"inputEstAddressId"}
                    className={(establishmentErrors.address ? "":"border-grey")+" w-full"}
                    value={establishmentItem.buildingaddress}
                    onChange={(e) => {
                        setEstablishmentItem({ ...establishmentItem, buildingaddress: e.target.value })
                        setEstablishmentErrors({ ...establishmentErrors, address: "" })
                    }}
                    error={!!establishmentErrors.address}
                    caption={establishmentErrors.address}
                />
            ),
        },
        {
            header: "Estado",
            className: "location-inputtable-fluid-col location-col-est-state",
            body: (item) => (
                <span>
                    {item.active ? "Activo" : "Inactivo"}
                </span>
            ),
            input: (
                <div className="flex align-items-center gap-2 mt-2 location-switch-inline-row location-desktop-inputtable-switch-row">
                    <InputSwitch
                        id="statusInputSwitchId"
                        className="w-full"
                        checked={establishmentItem.active}
                        onChange={(e) => setEstablishmentItem({ ...establishmentItem, active: e.value })}
                    />
                    <span className="text-sm location-switch-state-label">{establishmentItem.active ? "Activo" : "Inactivo"}</span>
                </div>
            ),
        },{
            header: "",
            className: "location-inputtable-control-column",
            input: (
                isEditing ? (
                    <LocationInlineEditActions
                        visible
                        onConfirm={() => { void handleAdd(); }}
                        onCancel={cancelEdit}
                    />
                ) : (
                    <Button
                    style={{marginTop:"20px"}}
                        type="button"
                        icon="pi pi-plus-circle text-3xl"
                        className="buttonSecondary2 p-button-rounded location-inputtable-add-inline-btn"
                        onClick={() => { void handleAdd(); }}
                        aria-label="Crear"
                    />
                )
            ),
            body: (item, index) => {
                const menuItems: LocationActionItem[] = [
                    {
                        label: "Editar",
                        icon: "pi pi-file-edit",
                        action: () => handleEdit(item, index),
                    },{
                        label: "Crearle un sector",
                        icon: "pi pi-plus",
                        action: () => handleAddSector(item),
                    },{
                        label: item.active ? "Desactivar" : "Activar",
                        icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                        action: () => handleToggleActive(item, index),
                    },{
                        label: "Eliminar",
                        icon: "pi pi-trash",
                        action: () => onDelete(index),
                    },
                ];

                return (
                    <div className="flex justify-content-end">
                        <LocationActionMenu
                            items={menuItems}
                            ariaLabel="Opciones del establecimiento"
                            buttonId="menuItemButtonId"
                            buttonClassName="p-button-text location-inputtable-menu-btn"
                        />
                    </div>
                );
            },
        },
    ];

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    async function saveFromMobileModal() {
        const result = await handleAdd();
        if (result !== false) {
            setMobileModalVisible(false);
        }
    }

    //border-divider hace que se vea el divider en la tabla
    if (isMobile) {
        return (
            <div className="location-config-view">
                <div className="location-mobile-section-header">
                    <h4 className="location-mobile-section-title">Establecimientos</h4>
                    <Button
                        className="location-mobile-add-btn"
                        icon="pi pi-plus-circle"
                        label="Agregar"
                        onClick={openAddModal}
                    />
                </div>

                <div className="location-mobile-card-list">
                    {(establishmentList ?? []).map((item, index) => {
                        const menuItems: LocationMobileActionItem[] = [
                            {
                                label: "Editar",
                                icon: "pi pi-file-edit",
                                action: () => openEditModal(item, index),
                            },
                            {
                                label: "Crearle un sector",
                                icon: "pi pi-plus",
                                action: () => handleAddSector(item),
                            },
                            {
                                label: item.active ? "Desactivar" : "Activar",
                                icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                                action: () => { void handleToggleActive(item, index); },
                            },
                            {
                                label: "Eliminar",
                                icon: "pi pi-trash",
                                action: () => onDelete(index),
                            },
                        ];

                        return (
                            <article key={`location-establishment-${item.buildingid}-${index}`} className="location-mobile-card">
                                <div className="location-mobile-card-header">
                                    <h4 className="location-mobile-card-title">{formatCardValue(item.buildingname, "Sin nombre")}</h4>
                                    <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del establecimiento" />
                                </div>

                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Dirección</span>
                                    <span className="location-mobile-card-value">{formatCardValue(item.buildingaddress)}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Estado</span>
                                    <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                                </div>
                            </article>
                        );
                    })}

                    {(establishmentList ?? []).length === 0 && (
                        <p className="location-mobile-empty">No hay establecimientos cargados.</p>
                    )}
                </div>

                <Modal
                    className="location-mobile-editor-modal"
                    visible={mobileModalVisible}
                    setVisible={setMobileModalVisible}
                    width="w-medium"
                    header={<h1 className="info-dark text-2xl">{establishmentItem.buildingid > 0 ? "Editar establecimiento" : "Agregar establecimiento"}</h1>}
                    footerButtonLeftText="Cancelar"
                    onClickLeftBtn={closeMobileModal}
                    footerButtonRightText={establishmentItem.buildingid > 0 ? "Guardar" : "Agregar"}
                    onClickRightBtn={() => { void saveFromMobileModal(); }}
                    onHideCustom={closeMobileModal}
                >
                    <div className="location-mobile-modal-content">
                        <div>
                            <label className="block mb-2">Nombre <span className="text-red-500">*</span></label>
                            <InputTextCustom
                                className={(establishmentErrors.name ? "" : "border-grey") + " w-full"}
                                value={establishmentItem.buildingname}
                                onChange={(e) => {
                                    setEstablishmentItem({ ...establishmentItem, buildingname: e.target.value });
                                    setEstablishmentErrors({ ...establishmentErrors, name: "" });
                                }}
                                error={!!establishmentErrors.name}
                                caption={establishmentErrors.name}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">Dirección <span className="text-red-500">*</span></label>
                            <InputTextCustom
                                className={(establishmentErrors.address ? "" : "border-grey") + " w-full"}
                                value={establishmentItem.buildingaddress}
                                onChange={(e) => {
                                    setEstablishmentItem({ ...establishmentItem, buildingaddress: e.target.value });
                                    setEstablishmentErrors({ ...establishmentErrors, address: "" });
                                }}
                                error={!!establishmentErrors.address}
                                caption={establishmentErrors.address}
                            />
                        </div>
                        <div className="flex align-items-center gap-2 location-switch-inline-row">
                            <InputSwitch
                                id="statusInputSwitchMobileId"
                                checked={establishmentItem.active}
                                onChange={(e) => setEstablishmentItem({ ...establishmentItem, active: e.value })}
                            />
                            <span className="location-switch-state-label">{establishmentItem.active ? "Activo" : "Inactivo"}</span>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }

    return (
        <InputTable
            className="location-inputtable-fluid location-inputtable-fluid--establishment"
            columnGap={0}
            headerDivClassName="font-semibold bg-bluegray-50"
            dividerClassName="my-1 border-divider"
            buttonColumnWidth={43}
            items={establishmentList ?? []}
            columns={columns}
            onAdd={() => {}}
            divBtnClassName="location-inputtable-hide-native-control"
            divider
            dividerInFirstItem
        />
    );
}

